import { useCallback, useEffect, useRef, useState } from 'react'

import { CONFIRM_INTERVAL_MS, CONFIRM_TRIES, deriveYourProgress, episodeCode, joinableId, sleep } from './watch.js'
import { TARGET_STOPPED, TARGET_WATCHED, TARGET_WATCHING } from './watchRequests.js'

// The archive — every catalog title, listed or not. V2-2b, screen 4
// ("Archive" — the brief said "Everything," a correction from Jordan
// mid-slice; see WatchArchive.jsx's own header) of
// docs/design-briefs/watch/v2/HANDOFF.md.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 A SECOND ENDPOINT, DELIBERATELY — `watch.js::useWatch` IS NOT THIS FILE
// ═══════════════════════════════════════════════════════════════════════════
//
// V2-2a's own report: `/api/watch/archive` is a SEPARATE route from `/api/watch`
// so the watch tab's own landing payload never grows with data it does not
// render. This module is the one reader of that route — the archive page,
// and nothing else (the member profile is V2-2c, after this slice).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 IT CANNOT THROW, AND THE PAGE MUST RENDER WITHOUT IT — `watch.js`'s own
//    rule, unchanged
// ═══════════════════════════════════════════════════════════════════════════

export const ARCHIVE_ENDPOINT = '/api/watch/archive'

export const LOADING = 'loading'
export const READY = 'ready'
export const UNAVAILABLE = 'unavailable'
export const MISCONFIGURED = 'misconfigured'

/** The status that means *this deployment is misconfigured*, and nothing
 * else — `watch.js::DEPLOYMENT_FAULT_STATUS`'s own reasoning, unchanged. */
export const DEPLOYMENT_FAULT_STATUS = 500

export function loadingArchive() {
  return { state: LOADING, shared: null }
}

export function unavailableArchive() {
  return { state: UNAVAILABLE, shared: null }
}

export function misconfiguredArchive() {
  return { state: MISCONFIGURED, shared: null }
}

/** The Function's answer, checked at the seam — `watch.js::buildWatch`'s
 * exact discipline, applied to this endpoint's own envelope. */
export function buildArchive(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return unavailableArchive()
  const shared = payload.shared
  if (!shared || typeof shared !== 'object' || Array.isArray(shared)) return unavailableArchive()
  return { state: READY, shared }
}

/**
 * Ask the Function. Resolves to an archive state, always.
 *
 * ⚠️ `credentials: 'same-origin'`: the session cookie is what makes this a
 * 200 rather than a 401. Callers here gate on `useSession()` first (the
 * page's own "as the rest of the portal does" sign-in door — see
 * `WatchArchive.jsx`), so an ordinary render never even reaches a 401;
 * this still degrades honestly if one somehow arrives.
 */
export async function fetchArchive(fetchImpl = globalThis.fetch) {
  if (typeof fetchImpl !== 'function') return unavailableArchive()
  try {
    const response = await fetchImpl(ARCHIVE_ENDPOINT, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) {
      return response.status === DEPLOYMENT_FAULT_STATUS ? misconfiguredArchive() : unavailableArchive()
    }
    return buildArchive(await response.json())
  } catch {
    return unavailableArchive()
  }
}

/**
 * The archive for one screen. Starts `loading`, lands `ready` or a failure
 * state — `watch.js::useWatch`'s single-fetch-on-mount shape (nothing here
 * moves on a two-second timescale), not `music.js::useMusic`'s poll.
 *
 * `enabled` is the caller's own `session.status === SIGNED_IN` — Trips.jsx's
 * exact pattern: "nothing is asked for until the session says there is
 * somebody to ask for." A signed-out visit never even reaches the Function;
 * the page's own sign-in door is what a real 401 would otherwise force this
 * module to interpret from the wire.
 *
 * 🔴 V2-2c: `refresh`/`confirm`, additive — `watch.js::useWatch`'s own
 * shape, mirrored rather than shared, the same "deliberately separate, not
 * unified" choice this file's own header already made for `fetchArchive`/
 * `buildArchive`/`loadingArchive` alongside `watch.js`'s `fetchWatch`/
 * `buildWatch`/`loadingWatch`: two endpoints, two independent fetchers,
 * one discipline. The member profile's own favourite-reorder buttons
 * (V2-2c) need to confirm a write against a FRESH archive — a favourite
 * can live on a title off the watchlist, so `/api/watch`'s own `confirm`
 * cannot see it — and `WatchArchive.jsx`'s existing call site
 * (`useWatchArchive(signedIn)`, no other arguments) is byte-for-byte
 * unaffected: it never destructures `refresh`/`confirm` at all.
 */
export function useWatchArchive(enabled) {
  const [archive, setArchive] = useState(loadingArchive)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const refresh = useCallback(() => {
    // No `.catch`: `fetchArchive` has no rejecting branch, and a handler
    // that can never run reads as if it could — `watch.js::useWatch`'s own
    // `refresh` states the identical rule.
    fetchArchive().then((answer) => {
      if (mounted.current) setArchive(answer)
    })
  }, [])

  const confirm = useCallback(async (isConfirmed) => {
    for (let attempt = 0; attempt < CONFIRM_TRIES; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop -- each attempt must see
      // the PREVIOUS one's answer before deciding whether to retry.
      const answer = await fetchArchive()
      if (!mounted.current) return false
      setArchive(answer)
      if (answer.state === READY && isConfirmed(answer.shared)) return true
      if (attempt < CONFIRM_TRIES - 1) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(CONFIRM_INTERVAL_MS)
        if (!mounted.current) return false
      }
    }
    return false
  }, [])

  useEffect(() => {
    if (!enabled) return
    refresh()
  }, [refresh, enabled])

  return { ...archive, refresh, confirm }
}

// ═══════════════════════════════════════════════════════════════════════════
// READING THE BLOB — one place, so the page and its tests cannot disagree
// ═══════════════════════════════════════════════════════════════════════════

/** Every title the blob carries, defensively. `[]` for anything else. */
export function titlesOf(shared) {
  const list = shared?.titles
  return Array.isArray(list) ? list.filter((row) => row && typeof row === 'object') : []
}

/** One title's `members[]`, defensively. */
function membersOf(title) {
  const list = title?.members
  return Array.isArray(list) ? list.filter((row) => row && typeof row === 'object') : []
}

/**
 * Whether this film has ever been shown at movie night — P6-303b. A
 * non-empty `movie_nights` (`utils/watch_projection.py::_movie_nights_view`'s
 * own field, published on this exact archive row) is the one source; this
 * reads only its length, never re-derives "was this a movie night pick"
 * from `kind` or anything else a row happens to carry. `false` for any
 * title that predates the field or was never shown there.
 */
export function shownAtMovieNight(title) {
  return Array.isArray(title?.movie_nights) && title.movie_nights.length > 0
}

/**
 * The crew's average rating for a title, or `null` when nobody has rated it
 * (or when every rating on file belongs to a member who has since stopped).
 *
 * 🔴 P6-291 (Jordan's ruling, 2026-09-25, question 2 in JORDAN HOLDS)
 * SUPERSEDES THE REASONING BELOW: a STOPPED member's rating is left out of
 * the crew average — it still shows on their own review and profile, just
 * not in this figure. WATCHING and WATCHED members both still count —
 * Jordan was asked specifically whether a watching member's rating should
 * count, and chose yes, so an airing show still gets a crew rating.
 *
 * The retired reasoning, kept because the correction only reads clearly
 * next to what it replaces: this function used to count EVERY member with
 * a rating, stopped included, arguing "a watching or stopped member's
 * rating is exactly as real as a watched one's" against the design
 * prototype's own finished-only mock (`fin`). That argument is superseded
 * by Jordan's own ruling, not wrong on the ground it stood on — the ruling
 * narrows it to exactly `stopped`, never wider than that.
 *
 * Mirrored, not shared, by the bot's own `crew_average_rating`
 * (`cogs/watch/helpers.py`) — CLAUDE.md gotcha 21: two implementations of
 * one contract, pinned against each other by the shared fixture
 * `site/src/test/fixtures/crew-average-cases.json`, never assumed to agree.
 *
 * A member with no `status` at all still counts — this excludes ONLY
 * `member.status === TARGET_STOPPED`, per the ruling's own "only `stopped`
 * is excluded... don't widen this." No path in the store ever produces a
 * member with no status; this is a defensive default for a shape this
 * module did not itself construct, the same discipline `memberIdsWhoWatched`
 * already applies one function down.
 */
export function crewAverageRating(title) {
  const ratings = membersOf(title)
    .filter((member) => member.status !== TARGET_STOPPED)
    .map((member) => member.rating)
    .filter((rating) => typeof rating === 'number' && Number.isFinite(rating))
  if (ratings.length === 0) return null
  return ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length
}

/**
 * Ids of every member who has "seen it at all" — the brief's own phrase for
 * the members filter, decided against the prototype's own union
 * (`e.whos.includes(w) || (this.sh(e.id).pos || {})[w]`: finished OR has a
 * position, i.e. any real status). The archive publishes a bare `status` per
 * member with no positional data (see `yourStatusLine`'s own header for why
 * that matters elsewhere) — "any status recorded at all" is the honest,
 * archive-only equivalent: watching, stopped or watched, all count.
 */
export function memberIdsWhoWatched(title) {
  return membersOf(title)
    .filter((member) => typeof member.status === 'string' && member.status !== '')
    .map((member) => joinableId(member.id))
    .filter((id) => id !== null)
}

/** The signed-in member's own row on this title, or `null`. */
export function yourArchiveEntry(title, userId) {
  if (typeof userId !== 'string' || userId === '') return null
  return membersOf(title).find((member) => joinableId(member.id) === userId) ?? null
}

/**
 * The viewer's own status line, in ember — the brief's own three worked
 * examples: "Finished · 9/10", "Watching · S2E4", "Stopped". `null` when the
 * title is not yours at all (never marked in), which the caller renders as
 * no line.
 *
 * 🔴 ROUND 2 (manager review): "Watching · S2E4" CANNOT BE BUILT FROM THE
 * ARCHIVE ALONE — `utils/watch_projection.py::_archive_title_view`
 * "deliberately excludes... progress" (its own docstring). For a title still
 * ON the watchlist, the position exists on `/api/watch`'s own summary
 * (P6-281's `members[].progress`), which the watch tab already loads —
 * `listedSubscription` is that summary row, passed by the caller when one
 * exists, `null`/`undefined` for an unlisted title. Never a second
 * derivation of the position itself: `deriveYourProgress` and `episodeCode`
 * are `watch.js`'s own, already used by the title page, reused verbatim
 * here rather than re-implemented (CLAUDE.md gotcha 1's *copies* axis).
 *
 * For an unlisted title (`listedSubscription` absent) the line stays bare —
 * "Watching", "Stopped" — never a guessed or fabricated episode code.
 * Finished always carries the rating (`rating` IS published per member,
 * archive or summary, unconditionally) whether or not the title is listed.
 */
export function yourStatusLine(title, userId, listedSubscription) {
  const mine = yourArchiveEntry(title, userId)
  if (!mine || typeof mine.status !== 'string') return null
  if (mine.status === TARGET_WATCHED) {
    return typeof mine.rating === 'number' && Number.isFinite(mine.rating)
      ? `Finished · ${mine.rating}/10`
      : 'Finished'
  }
  if (mine.status === TARGET_WATCHING || mine.status === TARGET_STOPPED) {
    const label = mine.status === TARGET_WATCHING ? 'Watching' : 'Stopped'
    const progress = listedSubscription ? deriveYourProgress(listedSubscription, userId) : null
    const code = progress ? episodeCode(progress.season, progress.episode) : null
    return code ? `${label} · ${code}` : label
  }
  return null
}

// ═══════════════════════════════════════════════════════════════════════════
// P6-298b — A SERIES, AS THE ARCHIVE SEES IT
// ═══════════════════════════════════════════════════════════════════════════
//
// The archive's OWN rows carry `series_id` (P6-298a) but no chain topology at
// all — `_archive_title_view`'s own docstring deliberately excludes every
// field `_subscription_view` publishes that this screen's design does not
// filter or show, and a franchise edge is exactly that kind of field.
// Reconstructing one from `title`/`year` text here would be the re-derivation
// CLAUDE.md gotcha 21 warns against (the bot's `_series_partition` is the
// ONE walk of `franchise.next`, never a second one). So this file reads chain
// ORDER only from the WATCH SUMMARY's own `series` map (`watchShared`,
// P6-298a — `WatchArchive.jsx` already borrows it for `yourStatusLine`'s own
// enrichment, signed in only) and degrades HONESTLY, never by guessing, when
// that is not available: a signed-out visitor, or the deploy-gap window
// before the bot's first publish of the field.
//
// The series' own NAME, by contrast, needs no summary at all: `series_id` is
// the chain's ROOT's own subscription id, and `_build_archive_payload`'s own
// widening guarantees the root's row is ALWAYS present in `titles[]` whenever
// any of its siblings are (a root's `series_root_by_id` is itself, so it
// passes the identical `touched_series_roots` filter its own sibling did) —
// so `seriesDisplayName` below is a pure, LOCAL lookup over the archive's own
// data, working identically signed in or out.

/**
 * The series' own name for a title's row — the ROOT season's own title — or
 * `null` when the title itself IS the root (nothing to prefix; render its
 * bare title exactly as before this slice) or when, defensively, the root
 * cannot be found in `titles` at all (should not happen — see the header —
 * but a missing prefix is a strictly safer failure than a wrong one).
 */
export function seriesDisplayName(titles, title) {
  const seriesId = typeof title?.series_id === 'string' && title.series_id ? title.series_id : null
  if (seriesId === null || seriesId === title?.id) return null
  const root = titles.find((row) => row.id === seriesId)
  return typeof root?.title === 'string' ? root.title : null
}

/**
 * P6-298b §2.3: under the title sort, a series' seasons sit TOGETHER, in
 * CHAIN order — never re-derived (see the header above). `watchShared` is
 * `/api/watch`'s own summary object (or `null`/anything else, defensively —
 * a signed-out visitor, or one whose summary has not loaded yet); its own
 * `series` map's `seasons` arrays are the ONLY source of chain order this
 * file will ever read.
 *
 * Each SERIES' overall position in the list is its ROOT's own title
 * (`seriesDisplayName`'s identical lookup, inlined here to sort by it) — so
 * the list stays alphabetical by FRANCHISE name, not by whichever member's
 * own title happens to sort first; a sequel titled nothing like its own
 * franchise (an English/Japanese title swap mid-chain) still sits with its
 * siblings rather than wherever its own text would otherwise land.
 *
 * WITHIN a series, a season absent from the summary's own chain data (no
 * `watchShared` at all, or this root's own entry missing) falls back to `id`
 * order — the store's own creation order (`utils/watch_manager.py::
 * _new_subscription_id`'s docstring: base36 ids "sort chronologically") —
 * rather than left in whatever order `titles` itself handed them in, which
 * carries no meaning at all for this purpose.
 *
 * A series of one (the overwhelming majority — no franchise link at all)
 * reduces to exactly one title in its own single-member "group", so this is
 * a pure widening of the plain alphabetical sort every pre-existing test
 * already pins: byte-identical output whenever nothing shares a `series_id`.
 */
export function groupArchiveTitlesByChain(titles, watchShared) {
  const byId = new Map(titles.map((title) => [title.id, title]))
  const seriesMap = watchShared?.series
  const validSeriesMap = seriesMap && typeof seriesMap === 'object' && !Array.isArray(seriesMap) ? seriesMap : {}

  const groupKeyOf = (title) =>
    typeof title.series_id === 'string' && title.series_id ? title.series_id : title.id

  const groups = new Map()
  for (const title of titles) {
    const key = groupKeyOf(title)
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(title)
  }

  const rootTitleText = (key) => {
    const root = byId.get(key)
    return typeof root?.title === 'string' ? root.title : ''
  }
  const orderedKeys = [...groups.keys()].sort((a, b) => rootTitleText(a).localeCompare(rootTitleText(b)))

  const result = []
  for (const key of orderedKeys) {
    const members = groups.get(key)
    const chainSeasons = Array.isArray(validSeriesMap[key]?.seasons) ? validSeriesMap[key].seasons : null
    const chainPosition = new Map()
    if (chainSeasons) {
      chainSeasons.forEach((season, index) => {
        if (season && typeof season === 'object' && typeof season.id === 'string') {
          chainPosition.set(season.id, index)
        }
      })
    }
    const ordered = [...members].sort((a, b) => {
      const positionA = chainPosition.has(a.id) ? chainPosition.get(a.id) : Number.MAX_SAFE_INTEGER
      const positionB = chainPosition.has(b.id) ? chainPosition.get(b.id) : Number.MAX_SAFE_INTEGER
      if (positionA !== positionB) return positionA - positionB
      return String(a.id).localeCompare(String(b.id))
    })
    result.push(...ordered)
  }
  return result
}

// ═══════════════════════════════════════════════════════════════════════════
// FILTERS — every option DERIVED from the loaded archive, never a hardcoded,
// date-relative literal (CLAUDE.md gotcha 23: a fixed year list rots the
// moment the calendar moves past it)
// ═══════════════════════════════════════════════════════════════════════════

/** The three kinds this store has ever supported (`utils/watch_manager.py`'s
 * own `KIND_TV`/`KIND_MOVIE`/`KIND_ANIME`, unchanged since W0) — a genuinely
 * closed, non-date-relative vocabulary, unlike the year list below. */
export const KINDS = Object.freeze(['tv', 'anime', 'movie'])

export const RATING_THRESHOLDS = Object.freeze(['9', '8', '7'])

export const ALL = 'all'
export const ANY = 'any'

/**
 * Every distinct real year present in the archive, newest first — never the
 * prototype's own hardcoded `2026, 2025, 2024, 2023, 2022, 2010s..., Before
 * 2010` list. That list is exactly the kind of fixed-to-today literal
 * CLAUDE.md gotcha 23 catalogues at length: it is correct on the day it is
 * typed and silently wrong on every day after. Deriving from the data itself
 * cannot go stale, and a title with no year is never offered as a bucket —
 * see `filterArchiveTitles`'s own header on how it is excluded, never
 * silently matched by numeric coercion.
 */
export function yearOptions(titles) {
  const years = new Set()
  for (const title of titles) {
    if (typeof title.year === 'number' && Number.isFinite(title.year)) years.add(title.year)
  }
  return [...years].sort((a, b) => b - a)
}

/**
 * Every member id who has seen ANYTHING in the whole archive, for the
 * members filter's own chip list — derived from the data rather than the
 * full member directory (which can carry server members who have never
 * touched the watch tab at all), and rather than the design prototype's own
 * fixed six-name `MEMBERS` array.
 */
export function memberIdsInArchive(titles) {
  const ids = new Set()
  for (const title of titles) {
    for (const id of memberIdsWhoWatched(title)) ids.add(id)
  }
  return ids
}

/**
 * The archive's own filter, in one place so the page and its tests read the
 * identical rule. `filters` is `{ kind, year, minRating, memberIds, query }`;
 * every field defaults to "no filter" when absent.
 *
 * 🔴 A TITLE WITH NO YEAR NEVER MATCHES A SPECIFIC YEAR — stated because
 * `null >= someNumber` and `null < someNumber` both coerce `null` to `0` in
 * JavaScript, so a careless numeric comparison would silently sort an
 * unknown year into "before" every bucket. This checks `typeof year ===
 * 'number'` FIRST and refuses the row outright rather than ever comparing a
 * `null` numerically. It still matches "All years" (P6-279: a title can
 * genuinely have no year yet — an unreleased film, a just-added anime).
 */
export function filterArchiveTitles(titles, filters = {}) {
  const kind = filters.kind ?? ALL
  const year = filters.year ?? ALL
  const minRating = filters.minRating ?? ANY
  const memberIds = Array.isArray(filters.memberIds) ? filters.memberIds : []
  const query = typeof filters.query === 'string' ? filters.query.trim().toLowerCase() : ''

  return titles.filter((title) => {
    if (kind !== ALL && title.kind !== kind) return false

    if (year !== ALL) {
      const y = title.year
      if (typeof y !== 'number' || !Number.isFinite(y)) return false
      if (y !== Number(year)) return false
    }

    if (minRating !== ANY) {
      const avg = crewAverageRating(title)
      if (avg === null || avg < Number(minRating)) return false
    }

    if (memberIds.length > 0) {
      const watchers = memberIdsWhoWatched(title)
      if (!memberIds.some((id) => watchers.includes(id))) return false
    }

    if (query !== '') {
      const titleText = typeof title.title === 'string' ? title.title.toLowerCase() : ''
      if (!titleText.includes(query)) return false
    }

    return true
  })
}

/**
 * The row's own summary line — kind of member counts, never the design
 * prototype's own "last finished" date (dropped from this slice's scope; see
 * the report). Member NAMES are the caller's own job (this returns only the
 * plain-text portion) — `<People>` needs `directory` and JSX, which a pure
 * helper should not reach for.
 */
export function summaryLineText(title) {
  const members = membersOf(title)
  const finishedCount = members.filter((member) => member.status === TARGET_WATCHED).length
  const watchingCount = members.filter((member) => member.status === TARGET_WATCHING).length
  const avg = crewAverageRating(title)

  const parts = [`${finishedCount} finished`]
  if (watchingCount > 0) parts.push(`${watchingCount} watching`)
  if (avg !== null) parts.push(`avg ${avg.toFixed(1)}`)
  if (title?.listed === false) parts.push('not on the list')
  return parts.join(' · ')
}

/** Other members (never you) who have seen it at all, capped at four — the
 * prototype's own `e.whos.filter(w => w !== ME).slice(0, 4)`, over the
 * archive-derived "seen it at all" set rather than the mock's `fin`. */
export function otherWatcherIds(title, userId) {
  return memberIdsWhoWatched(title)
    .filter((id) => id !== userId)
    .slice(0, 4)
}
